import { post } from "storefront/GrailedAPI/request";
import GrailedAPIError from "storefront/GrailedAPI/Error";
import {
  nextActionFromErrorBody,
  parseNextAction,
  TwoFactorChallenge,
} from "storefront/GrailedAPI/NextAction";

type Response = {
  data: {
    nextAction: unknown;
  };
};

export type ResendSignInCodeFailure =
  | { type: "ResendUnavailable"; challenge: TwoFactorChallenge }
  | { type: "SignInAgain" }
  | { type: "TooManyRequests" }
  | { type: "Unknown" };

const DEFAULT_MESSAGES: Record<ResendSignInCodeFailure["type"], string> = {
  ResendUnavailable: "A new code can't be sent right now.",
  SignInAgain: "Your verification code has expired. Please sign in again.",
  TooManyRequests: "Too many requests. Please try again later.",
  Unknown: "We couldn't send a new code. Please try again.",
};

export class ResendSignInCodeError extends Error {
  failure: ResendSignInCodeFailure;

  constructor(failure: ResendSignInCodeFailure, message?: string) {
    super(message || DEFAULT_MESSAGES[failure.type]);
    this.name = "ResendSignInCodeError";
    this.failure = failure;
  }
}

const errorFromReason = (reason: unknown): ResendSignInCodeError => {
  if (!(reason instanceof GrailedAPIError)) {
    return new ResendSignInCodeError({ type: "Unknown" });
  }

  const nextAction = nextActionFromErrorBody(reason.body);
  const serverMessage = reason.body.error?.message;

  if (nextAction?.type === "sign_in") {
    return new ResendSignInCodeError({ type: "SignInAgain" }, serverMessage);
  }

  if (nextAction?.type === "two_factor_challenge") {
    return new ResendSignInCodeError(
      { type: "ResendUnavailable", challenge: nextAction.twoFactorChallenge },
      serverMessage,
    );
  }

  if (reason.response.status === 429) {
    return new ResendSignInCodeError({ type: "TooManyRequests" });
  }

  return new ResendSignInCodeError({ type: "Unknown" });
};

/**
 * @name GrailedAPI.v1.Sessions.resend
 * @description Sends a new code for a sign in that returned `two_factor_required`. Resolves with
 * the updated challenge, and rejects with a ResendSignInCodeError describing why a new code
 * couldn't be sent.
 */
const resend = async (challengeToken: string): Promise<TwoFactorChallenge> => {
  let response: Response;

  try {
    response = await post<Response>("/api/sign_in/resend", { challengeToken });
  } catch (reason: unknown) {
    throw errorFromReason(reason);
  }

  const nextAction = parseNextAction(response.data.nextAction);

  if (nextAction?.type !== "two_factor_challenge") {
    throw new ResendSignInCodeError({ type: "Unknown" });
  }

  return nextAction.twoFactorChallenge;
};

export default resend;
